import React, { useEffect, useMemo, useState } from 'react';
import { ComposedChart, Area, Line, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip } from 'recharts';
import { useChartHover, formatTick } from './chartHover';
import { FC_GROUPS, toPoints, visibleColumns, groupTitle, chartSeries, filterByDate, aggregate30, aggLabel } from '../../utils/fieldclimateGroups';

const API_URL = process.env.REACT_APP_API_BASE_URL || 'http://169.58.21.41/api';
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('auth_token')}` });

const pad2 = (n) => String(n).padStart(2, '0');
const fmtX = (iso) => {
    const d = new Date(iso);
    return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
};
const fmtVal = (v, unit) => (v == null || Number.isNaN(v) ? '—' : Number(v).toFixed(unit === 'mV' || unit === 'W/m²' ? 0 : unit === 'kPa' ? 2 : 1));
const isNum = (v) => typeof v === 'number' && !Number.isNaN(v);

// One chart per measurement: a line per value in the table (max solid, min dashed) with the
// max–min range shaded between them.
function MeasurementChart({ column, pts, lang }) {
    const s = useMemo(() => chartSeries(column), [column]);
    const data = useMemo(() => {
        const rows = column.chart === 'daily' ? pts.filter(p => s.lines.some(l => isNum(p[l.field]))) : pts;
        return rows.map(p => {
            const row = { t: p.t };
            s.lines.forEach((l, i) => { row[`v${i}`] = p[l.field] ?? null; });
            if (s.band) row.band = isNum(p[s.band.max]) && isNum(p[s.band.min]) ? [p[s.band.min], p[s.band.max]] : null;
            return row;
        });
    }, [pts, column, s]);

    const { wrapRef, tipRef, lineRef, chartProps } = useChartHover({
        length: data.length,
        tipWidth: 150,
        renderTip: (idx) => {
            const p = data[idx];
            const parts = s.lines.map((l, i) => ({ label: aggLabel(l.label, lang), v: p[`v${i}`] })).filter(x => isNum(x.v));
            if (!parts.length) return null;
            const dot = `<span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${column.color};margin-right:6px;vertical-align:middle"></span>`;
            const rows = parts.map(x => `<div class="chart-tip-val" style="color:#f8fafc;font-size:15px">${dot}${s.lines.length > 1 ? `<span style="font-size:10px;color:#94a3b8;margin-right:4px">${x.label}</span>` : ''}${fmtVal(x.v, column.unit)}<span class="chart-tip-unit">${column.unit}</span></div>`).join('');
            return `<div class="chart-tip-time">${fmtX(p.t)}</div>${rows}`;
        },
    });

    const label = lang === 'en' ? column.en : column.tr;
    const values = data.flatMap(d => s.lines.map((_, i) => d[`v${i}`])).filter(isNum);
    const lo = Math.min(...values);
    const yMin = lo >= 0 && lo <= (Math.max(...values) - lo) / 2 ? 0 : 'auto';
    const subtitle = s.lines.map(l => aggLabel(l.label, lang)).join(' / ');

    return (
        <div ref={wrapRef} className="chart-card">
            <div className="chart-card-title">
                {label}<span className="chart-card-unit"> ({column.unit} · {subtitle})</span>
            </div>
            <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={data} margin={{ top: 4, right: 4, left: 0, bottom: 0 }} {...chartProps}>
                    <CartesianGrid strokeDasharray="2 4" stroke="rgba(148,163,184,0.15)" vertical={false} />
                    <XAxis dataKey="t" tickFormatter={fmtX} tick={{ fontSize: 9, fill: '#94a3b8' }} minTickGap={70} axisLine={false} tickLine={false} />
                    <YAxis domain={[yMin, 'auto']} width={44} tick={{ fontSize: 9, fill: '#94a3b8' }} axisLine={false} tickLine={false} tickCount={5} tickFormatter={formatTick} />
                    <Tooltip content={() => null} cursor={false} isAnimationActive={false} />
                    {column.chart === 'bar' ? (
                        <Bar dataKey="v0" fill={column.color} isAnimationActive={false} />
                    ) : (
                        <>
                            {s.band && <Area type="monotone" dataKey="band" stroke="none" fill={column.color} fillOpacity={0.12} isAnimationActive={false} connectNulls />}
                            {s.lines.map((l, i) => (
                                <Line key={l.field} type="monotone" dataKey={`v${i}`} stroke={column.color}
                                    strokeWidth={l.label === 'min' ? 1.4 : 1.8} strokeDasharray={l.label === 'min' ? '4 3' : undefined}
                                    dot={column.chart === 'daily' ? { r: 3, fill: column.color } : false} isAnimationActive={false} connectNulls />
                            ))}
                        </>
                    )}
                </ComposedChart>
            </ResponsiveContainer>
            <div ref={lineRef} className="chart-crosshair" />
            <div ref={tipRef} className="chart-tip" />
        </div>
    );
}

function exportCsv(rows, columns, lang, fileName) {
    const head = ['Date', 'Time', ...columns.flatMap(c => c.aggs.map(a => `${lang === 'en' ? c.en : c.tr} [${c.unit}] ${aggLabel(a.label, lang)}`))];
    const body = rows.map(r => {
        const d = new Date(r.t);
        return [`${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`, `${pad2(d.getHours())}:${pad2(d.getMinutes())}`,
            ...columns.flatMap(c => c.aggs.map(a => (isNum(r[a.field]) ? r[a.field] : '')))].join(',');
    });
    const blob = new Blob([[head.map(h => `"${h}"`).join(','), ...body].join('\n')], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${fileName}_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
}

// FieldClimate-style table: measurement header spanning its aggregate sub-columns.
function GroupTable({ pts, columns, lang, fileName }) {
    const [page, setPage] = useState(1);
    const PER_PAGE = 25;
    const rows = useMemo(() => [...pts].reverse(), [pts]);
    const pages = Math.max(1, Math.ceil(rows.length / PER_PAGE));
    const pageRows = rows.slice((page - 1) * PER_PAGE, page * PER_PAGE);
    useEffect(() => setPage(1), [pts]);

    const th = { padding: '7px 10px', fontSize: 11, fontWeight: 700, color: '#475569', background: '#f8fafc', borderBottom: '1px solid #e5e7eb', borderLeft: '1px solid #e5e7eb', whiteSpace: 'nowrap', textAlign: 'left' };
    const td = { padding: '6px 10px', fontSize: 12, color: '#334155', borderBottom: '1px solid #f1f5f9', borderLeft: '1px solid #f1f5f9', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums', fontFamily: "'IBM Plex Mono', monospace" };

    return (
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden', marginTop: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', borderBottom: '1px solid #f1f5f9', flexWrap: 'wrap' }}>
                <div style={{ fontWeight: 700, fontSize: 13, flex: 1, color: '#111827' }}>
                    {lang === 'en' ? 'History' : 'Geçmiş'} ({rows.length} {lang === 'en' ? 'records' : 'kayıt'})
                </div>
                <button onClick={() => exportCsv(rows, columns, lang, fileName)}
                    style={{ background: '#6366f1', color: '#fff', border: 'none', borderRadius: 8, padding: '6px 14px', cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>
                    CSV
                </button>
            </div>
            <div style={{ overflowX: 'auto' }}>
                <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                    <thead>
                        <tr>
                            <th rowSpan={2} style={{ ...th, borderLeft: 'none' }}>{lang === 'en' ? 'Date / time' : 'Tarih / saat'}</th>
                            {columns.map(c => <th key={c.key} colSpan={c.aggs.length} style={th}>{lang === 'en' ? c.en : c.tr} [{c.unit}]</th>)}
                        </tr>
                        <tr>
                            {columns.flatMap(c => c.aggs.map(a => <th key={c.key + a.label} style={{ ...th, fontWeight: 600, color: '#64748b' }}>{aggLabel(a.label, lang)}</th>))}
                        </tr>
                    </thead>
                    <tbody>
                        {pageRows.map(r => (
                            <tr key={r.t}>
                                <td style={{ ...td, borderLeft: 'none', fontFamily: 'inherit' }}>{fmtX(r.t)}</td>
                                {columns.flatMap(c => c.aggs.map(a => <td key={c.key + a.label} style={td}>{fmtVal(r[a.field], c.unit)}</td>))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {pages > 1 && (
                <div style={{ display: 'flex', justifyContent: 'center', gap: 8, padding: 10, alignItems: 'center', fontSize: 12, color: '#64748b' }}>
                    <button disabled={page === 1} onClick={() => setPage(p => p - 1)} style={{ background: '#f1f5f9', border: 'none', borderRadius: 6, padding: '4px 12px', cursor: 'pointer' }}>←</button>
                    {page} / {pages}
                    <button disabled={page === pages} onClick={() => setPage(p => p + 1)} style={{ background: '#f1f5f9', border: 'none', borderRadius: 6, padding: '4px 12px', cursor: 'pointer' }}>→</button>
                </div>
            )}
        </div>
    );
}

export default function FieldClimateGroupDetail({ sensor, language = 'tr' }) {
    const lang = language === 'en' ? 'en' : 'tr';
    const group = FC_GROUPS[sensor.fcGroup];
    const [days, setDays] = useState(7);
    // Custom range (YYYY-MM-DD, local calendar) overrides the preset buttons when set.
    const [start, setStart] = useState('');
    const [end, setEnd] = useState('');
    const custom = !!(start || end);
    const [readings, setReadings] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        const to = end ? new Date(`${end}T23:59:59.999`) : new Date();
        const from = start ? new Date(`${start}T00:00:00`) : new Date(to.getTime() - days * 86400000);
        if (from > to) return;
        setLoading(true);
        setError(null);
        fetch(`${API_URL}/sensors/${sensor.id}/history?from=${from.toISOString()}&to=${to.toISOString()}`, { headers: authHeaders() })
            .then(r => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
            .then(data => setReadings(Array.isArray(data) ? data : []))
            .catch(e => setError(e.message))
            .finally(() => setLoading(false));
    }, [sensor.id, days, start, end]);

    // Pick columns on the raw 15-min data first (this is where average fallbacks are decided),
    // then merge into 30-min rows using exactly those columns.
    const rawPts = useMemo(() => filterByDate(toPoints(readings), start, end), [readings, start, end]);
    const columns = useMemo(() => visibleColumns(group, rawPts), [group, rawPts]);
    const pts = useMemo(() => aggregate30(rawPts, columns), [rawPts, columns]);
    const lastEt0 = isNum(sensor.latestData?.ET0) ? sensor.latestData : null;
    const pickPreset = (d) => { setStart(''); setEnd(''); setDays(d); };
    const dateInput = { border: '1px solid #e5e7eb', borderRadius: 6, padding: '3px 6px', fontSize: 11, color: '#374151' };
    const stationName = (sensor.name || '').replace(/^Meteo - FieldClimate\s*/i, '');

    return (
        <div style={{ padding: '2px 0' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
                <span style={{ fontWeight: 800, fontSize: 16, color: '#111827' }}>{groupTitle(sensor.fcGroup, lang)}</span>
                <span style={{ fontSize: 13, color: '#64748b' }}>· {stationName}{sensor.fieldName ? ` · ${sensor.fieldName}` : ''}</span>
                <span style={{ background: '#ccfbf1', color: '#115e59', fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20 }}>FieldClimate · 30 {lang === 'en' ? 'min' : 'dk'}</span>
                {sensor.fcGroup === 'agro' && (
                    <span title={lang === 'en' ? 'Latest daily ET₀ reported by the station' : 'İstasyonun gönderdiği en son günlük ET₀'}
                        style={{ background: '#f0fdfa', color: '#0f766e', border: '1px solid #99f6e4', fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 20 }}>
                        {lang === 'en' ? 'Latest ET₀' : 'Son ET₀'}: {lastEt0
                            ? `${lastEt0.ET0} mm${lastEt0.ET0_at ? ` · ${new Date(lastEt0.ET0_at).toLocaleDateString(lang === 'en' ? 'en-GB' : 'tr-TR')}` : ''}`
                            : '—'}
                    </span>
                )}
                <div style={{ marginLeft: 'auto', display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                    {[1, 7, 30].map(d => {
                        const active = !custom && days === d;
                        return (
                            <button key={d} onClick={() => pickPreset(d)} style={{
                                padding: '4px 12px', borderRadius: 20, fontSize: 11, fontWeight: 700, cursor: 'pointer',
                                border: active ? '2px solid #6366f1' : '1px solid #e5e7eb',
                                background: active ? '#6366f1' : '#fff', color: active ? '#fff' : '#6b7280',
                            }}>{d === 1 ? (lang === 'en' ? '24 h' : '24 sa') : `${d} ${lang === 'en' ? 'days' : 'gün'}`}</button>
                        );
                    })}
                    <span style={{ fontSize: 11, color: '#94a3b8', marginLeft: 6 }}>{lang === 'en' ? 'or' : 'ya da'}</span>
                    <input type="date" value={start} max={end || undefined} onChange={e => setStart(e.target.value)} aria-label={lang === 'en' ? 'Start date' : 'Başlangıç tarihi'}
                        style={{ ...dateInput, borderColor: custom ? '#6366f1' : '#e5e7eb' }} />
                    <span style={{ fontSize: 11, color: '#94a3b8' }}>→</span>
                    <input type="date" value={end} min={start || undefined} onChange={e => setEnd(e.target.value)} aria-label={lang === 'en' ? 'End date' : 'Bitiş tarihi'}
                        style={{ ...dateInput, borderColor: custom ? '#6366f1' : '#e5e7eb' }} />
                </div>
            </div>

            {loading && <div style={{ textAlign: 'center', padding: 36, color: '#6b7280', fontSize: 13 }}>{lang === 'en' ? 'Loading…' : 'Yükleniyor…'}</div>}
            {error && <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 14px', color: '#dc2626', fontSize: 13 }}>{error}</div>}
            {!loading && !error && pts.length === 0 && (
                <div style={{ textAlign: 'center', padding: 36, color: '#9ca3af', fontSize: 13 }}>{lang === 'en' ? 'No data in this range' : 'Bu aralıkta veri yok'}</div>
            )}

            {!loading && pts.length > 0 && (
                <>
                    <div className="chart-grid">
                        {columns.map(c => <MeasurementChart key={c.key} column={c} pts={pts} lang={lang} />)}
                    </div>
                    <GroupTable pts={pts} columns={columns} lang={lang}
                        fileName={`${stationName}_${sensor.fcGroup}${custom ? `_${start || 'start'}_${end || 'today'}` : ''}`} />
                </>
            )}
        </div>
    );
}
